import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InputField } from "../../components/ui/InputField";
import Button from "../../components/ui/Button";
import AuthLayout from "../../components/layouts/AuthLayout";
import { AuthAPI } from "../../services/api";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  // Form data
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get("token");
      const email = searchParams.get("email");

      if (!token || !email) {
        setTokenValid(false);
        setError("Invalid reset link. Please request a new password reset.");
        return;
      }

      try {
        const data = await AuthAPI.validateResetToken({ token, email });
        if (data.success) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(
            data.message ||
              "Your password reset request is incorrect. Please check your email again."
          );
        }
      } catch (err) {
        setTokenValid(false);
        setError("Network error. Please try again later.");
      }
    };

    validateToken();
  }, [searchParams]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) return;

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = searchParams.get("token");
      const email = searchParams.get("email");

      const data = await AuthAPI.resetPassword({
        token: token!,
        email: email!,
        password,
        password_confirmation: confirmPassword,
      });

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignInRedirect = () => {
    navigate("/auth/signin");
  };

  const handleTryAgain = () => {
    navigate("/auth/forgot-password");
  };

  const renderPasswordForm = () => (
    <>
      <form
        onSubmit={handlePasswordReset}
        className="flex relative flex-col gap-1.5 items-start z-[2] w-[80%]"
      >
        <div className="text-lg font-semibold text-[#403634]">
          PLEASE CHANGE & REMEMBER YOUR PASSWORD
        </div>
        <InputField
          label="Type new password here"
          placeholder="Enter new password"
          type="password"
          value={password}
          onChange={setPassword}
        />

        <InputField
          label="Confirm new password"
          placeholder="Re-enter new password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
        />
      </form>

      {error && (
        <div className="relative self-stretch text-sm text-red-600 text-center z-[2]">
          {error}
        </div>
      )}

      <Button
        onClick={handlePasswordReset}
        disabled={loading || !password || !confirmPassword}
        size="sm"
        variant="primary"
        className="z-[2]"
        loading={loading}
      >
        Reset Password
      </Button>

      <div className="flex relative gap-2.5 justify-center items-start self-stretch z-[2]">
        <span className="text-sm text-stone-500 max-sm:text-xs">
          Remember your password?
        </span>
        <button
          type="button"
          onClick={handleSignInRedirect}
          className="text-sm font-semibold cursor-pointer text-stone-900 max-sm:text-xs hover:text-stone-700 transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    </>
  );

  const renderSuccessState = () => (
    <>
      <div className="relative text-center z-[2] space-y-4">
        <div className="text-lg font-semibold text-[#403634]">
          WHOHOO! YOUR PASSWORD WAS SUCCESSFULLY SET!
        </div>
        <div className="text-sm text-[#403634]">
          Now you can get back to the journey. Please back to the sign-in
          section with the button below
        </div>
      </div>

      <Button
        onClick={handleSignInRedirect}
        size="sm"
        variant="primary"
        className="z-[2]"
      >
        Continue to your account
      </Button>
    </>
  );

  const renderErrorState = () => (
    <>
      <div className="relative text-center z-[2] space-y-4">
        <div className="text-lg font-semibold text-[#403634]">
          INVALID RESET LINK
        </div>
        <div className="text-4xl">❌</div>
        <div className="text-sm text-[#403634]">{error}</div>
      </div>

      <Button
        onClick={handleTryAgain}
        size="sm"
        variant="primary"
        className="z-[2]"
      >
        Request New Reset Link
      </Button>

      <div className="flex relative gap-2.5 justify-center items-start self-stretch z-[2]">
        <span className="text-sm text-stone-500 max-sm:text-xs">
          Need help?
        </span>
        <button
          type="button"
          onClick={handleSignInRedirect}
          className="text-sm font-semibold cursor-pointer text-stone-900 max-sm:text-xs hover:text-stone-700 transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    </>
  );

  const getTitle = () => {
    if (success) return "";
    if (tokenValid === false) return "";
    return "";
  };

  const renderContent = () => {
    if (tokenValid === null) {
      // Loading state while validating token
      return (
        <div className="relative text-center z-[2] space-y-4">
          <div className="text-lg font-semibold text-[#403634]">
            Validating reset link...
          </div>
        </div>
      );
    }

    if (success) {
      return renderSuccessState();
    }

    if (tokenValid === false) {
      return renderErrorState();
    }

    return renderPasswordForm();
  };

  return <AuthLayout title={getTitle()}>{renderContent()}</AuthLayout>;
}

export default ResetPasswordPage;
